import React, { useCallback, useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import { api } from '../../lib/api';
import { localPart } from '../../lib/communications';

// Mailboxes tab → "Send-as permissions": who may send email from which
// mailbox. Rows = staff, columns = mailboxes. Owner cells are always on and
// locked (the server refuses to change them with 409 `owner`). Admins toggle
// with an optimistic update that rolls back on error; ops see it read-only.

const URL = '/api/admin/communications/send-as-permissions';
const ROLE_LABELS = { nexsav_admin: 'Admin', ops: 'Ops' };

export default function SendAsPermissionsGrid({ isAdmin, onToast }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState({}); // `${staffId}:${mailboxId}` → true

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await api.get(URL);
      setData(res || null);
    } catch (err) {
      setError(err?.message || 'Failed to load send-as permissions');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (staffId, mailboxId, next) => {
    const key = `${staffId}:${mailboxId}`;
    const prev = data?.grid?.[staffId]?.[mailboxId];
    setData((d) => d ? ({ ...d, grid: { ...d.grid, [staffId]: { ...(d.grid[staffId] || {}), [mailboxId]: next } } }) : d);
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.put(URL, { grantee_user_id: staffId, mailbox_id: mailboxId, allowed: next });
    } catch (err) {
      setData((d) => d ? ({ ...d, grid: { ...d.grid, [staffId]: { ...(d.grid[staffId] || {}), [mailboxId]: prev } } }) : d);
      onToast?.({ message: 'Couldn’t change that permission', detail: err?.message });
    } finally {
      setSaving((s) => { const n = { ...s }; delete n[key]; return n; });
    }
  };

  const staff = data?.staff || [];
  const mailboxes = data?.mailboxes || [];

  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] mb-5" data-testid="send-as-permissions">
      <div className="px-4 pt-3.5 pb-3 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Send-as permissions</div>
          <div className="text-[12px] text-gray-500 mt-1 max-w-2xl">
            Who may send email from which mailbox. Everyone can always send as themselves. Every send records both the mailbox and the person.
            {!isAdmin && <span className="text-gray-600"> Only admins can change these.</span>}
          </div>
        </div>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-white/[0.08] text-[10px] uppercase tracking-wider text-gray-400 whitespace-nowrap">Default: everyone may send as anyone</span>
      </div>

      {error && <div className="mx-4 mb-3 text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-2.5">{error}</div>}

      {!data && !error ? (
        <div className="px-4 pb-4 animate-pulse"><div className="h-24 rounded-lg bg-white/[0.03]" /></div>
      ) : mailboxes.length === 0 ? (
        <div className="px-4 pb-4 text-[12px] text-gray-500">No connected mailboxes yet — the grid fills in as people connect Gmail.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px] border-t border-white/[0.06]">
            <thead>
              <tr className="text-left text-gray-500">
                <th scope="col" className="px-4 py-2.5 font-normal text-[11px] uppercase tracking-wider whitespace-nowrap">Staff member ↓ may send as →</th>
                {mailboxes.map((m) => (
                  <th key={m.id} scope="col" className="px-4 py-2.5 font-normal whitespace-nowrap align-top">
                    <div className="text-gray-200">{localPart(m.email)}</div>
                    {m.aliases?.length > 0 && (
                      <div className="text-[10.5px] text-gray-500 mt-0.5">{m.aliases.map(localPart).join(' · ')}</div>
                    )}
                    {m.status !== 'connected' && <div className="text-[10.5px] text-amber-300/80 mt-0.5">not connected</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.id} className="border-t border-white/[0.05]" data-testid={`perm-row-${s.id}`}>
                  <th scope="row" className="px-4 py-2.5 text-left font-normal whitespace-nowrap">
                    <div className="text-gray-100">{s.name}</div>
                    <div className="text-[10.5px] text-gray-500">{ROLE_LABELS[s.role] || s.role}</div>
                  </th>
                  {mailboxes.map((m) => {
                    const owner = m.ownerUserId === s.id;
                    const on = owner ? true : !!data.grid?.[s.id]?.[m.id];
                    const key = `${s.id}:${m.id}`;
                    const locked = owner || !isAdmin;
                    return (
                      <td key={m.id} className="px-4 py-2.5">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={on}
                          aria-label={`${s.name} may send as ${m.email}`}
                          title={owner ? 'Own mailbox' : undefined}
                          disabled={locked || !!saving[key]}
                          onClick={() => !locked && toggle(s.id, m.id, !on)}
                          data-testid={`perm-${s.id}-${m.id}`}
                          data-locked={owner ? 'true' : undefined}
                          className={`relative inline-flex items-center w-9 h-5 rounded-full border transition-colors ${
                            on ? 'bg-[#B0FC38]/80 border-[#B0FC38]/60' : 'bg-white/[0.06] border-white/[0.12]'
                          } ${locked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:brightness-110'} disabled:opacity-60`}
                        >
                          <span aria-hidden="true" className={`absolute top-0.5 w-3.5 h-3.5 rounded-full transition-all ${on ? 'left-[18px] bg-gray-900' : 'left-0.5 bg-gray-300'}`} />
                          {owner && <Lock className="absolute -right-4 w-3 h-3 text-gray-500" aria-hidden="true" />}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
